import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { HttpError, type CardDetail } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { conversationRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useCardCustomerProfileOf } from '@web/ui/core/hooks';
import { ConversationThread, NotFoundPage, TabBar } from '@web/ui/core/organisms';
import { twcn } from '@web/ui/core/utils';

import { HandoffSummary } from '../components/handoff_summary';
import { TravelerSide, type TravelerAccess } from '../components/traveler_side';
import { TravelerViewHeader } from '../components/traveler_view_header';
import { useConversationCard } from '../hooks/use_conversation_card';

type NarrowTab = 'conversation' | 'traveler';

function hasStatus(err: unknown, ...statuses: number[]): boolean {
  return err instanceof HttpError && statuses.includes(err.status);
}

/**
 * Whether the right half can show the traveler. The kanban guards key on the
 * card's numeric LEGACY customer id, so an anonymous / handle id has no data.
 */
function travelerAccess(cardQuery: {
  isError: boolean;
  error: unknown;
  data: CardDetail | undefined;
}): TravelerAccess {
  if (cardQuery.isError) {
    if (hasStatus(cardQuery.error, 404)) return 'none';
    return hasStatus(cardQuery.error, 403) ? 'forbidden' : 'error';
  }
  if (cardQuery.data === undefined) return 'loading';
  return /^\d+$/.test(cardQuery.data.customer.id) ? 'ready' : 'none';
}

/**
 * `/orgs/$orgSlug/conversations/$conversationId` — the traveler view the
 * Zendesk handoff ticket links to. Read-only and full screen: the conversation
 * on the left half, the traveler and their trips on the right; below `xl` the
 * halves become two tabs. Keyed by the conversation id, resolved to the card
 * (kanban) for the traveler's data.
 */
export function TravelerViewPage(): React.JSX.Element {
  const { conversationId = '' } = useParams({ strict: false });
  const metaQuery = useQuery({
    queryKey: queryKeys.conversation.meta(conversationId),
    queryFn: () => conversationRepository.loadMeta(conversationId),
    enabled: conversationId !== '',
    retry: (count, err) => !hasStatus(err, 403, 404) && count < 1,
    retryDelay: 0,
    meta: { silent: true },
  });
  if (metaQuery.isError && hasStatus(metaQuery.error, 403, 404)) return <NotFoundPage />;
  return (
    <TravelerViewLayout
      conversationId={conversationId}
      conversationTripId={metaQuery.data?.tripId}
    />
  );
}

function TravelerViewLayout({
  conversationId,
  conversationTripId,
}: Readonly<{
  conversationId: string;
  conversationTripId: string | undefined;
}>): React.JSX.Element {
  const [tab, setTab] = useState<NarrowTab>('conversation');
  // By conversation, never `useCardDetail`: a guest outside the card's squad
  // opens this page from the ticket, and the by-id read would 403 them.
  const cardQuery = useConversationCard(conversationId);
  const profileState = useCardCustomerProfileOf(cardQuery);
  const detail = cardQuery.data;
  const access = travelerAccess(cardQuery);
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <TravelerViewHeader
        detail={detail}
        profile={profileState.status === 'ready' ? profileState.profile : undefined}
        access={access}
      />
      <div className="bg-surface xl:hidden">
        <TabBar ariaLabel={t('travelerView.tabs.aria')}>
          <TabBar.Tab
            icon="chat-circle-text"
            label={t('travelerView.tabs.conversation')}
            isActive={tab === 'conversation'}
            onClick={() => {
              setTab('conversation');
            }}
          />
          <TabBar.Tab
            icon="user"
            label={t('travelerView.tabs.traveler')}
            isActive={tab === 'traveler'}
            onClick={() => {
              setTab('traveler');
            }}
          />
        </TabBar>
      </div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <section
          aria-label={t('travelerView.tabs.conversation')}
          className={twcn(
            'min-h-0 flex-1 flex-col overflow-hidden border-r border-border-soft bg-surface xl:flex xl:w-1/2',
            tab === 'conversation' ? 'flex' : 'hidden',
          )}
        >
          {detail ? <HandoffSummary subject={detail.subject} summary={detail.summary} /> : null}
          <ConversationThread conversationId={conversationId} canReply={false} />
        </section>
        <section
          aria-label={t('travelerView.tabs.traveler')}
          className={twcn(
            'min-h-0 flex-1 overflow-y-auto xl:block xl:w-1/2',
            tab === 'traveler' ? 'block' : 'hidden',
          )}
        >
          <TravelerSide
            access={access}
            conversationId={conversationId}
            customerId={detail?.customer.id}
            profileState={profileState}
            conversationTripId={conversationTripId}
          />
        </section>
      </div>
    </div>
  );
}
